import { useGetAllSeasonsCategories } from '@hts/features/libs';
import { ENotAuthNavigation } from '@hts/shared/enums';
import { IGetStaticElementsResponse, IMappedSubmenuCategory } from '@hts/shared/types';
import { HtsButtonIcon, HtsLink, HtsTypography, HtslIcon } from '@hts/shared/ui';
import { SwipeableDrawer } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import styles from '../../../styles/Header/Mobile/AllSeasonsMenu.module.scss';

interface IRenderCategoryState extends IMappedSubmenuCategory {
  parentId?: number;
}

interface IAllSeasonsMenuProps {
  isOpen: boolean;
  staticElements?: IGetStaticElementsResponse;
  onOpen: () => void;
  onClose: () => void;
  onCloseDrawer: () => void;
}

export const AllSeasonsMenu: React.FC<IAllSeasonsMenuProps> = memo(
  ({ isOpen, staticElements, onClose, onOpen, onCloseDrawer }) => {
    const categories = useGetAllSeasonsCategories(staticElements?.menus);

    const renderCategories = useMemo(() => {
      const categoryMap = new Map<number, IRenderCategoryState>();

      categories.forEach((category) => {
        categoryMap.set(category.id, {
          ...category,
          parentId: undefined,
          categories: category.categories.map((subCategory) => ({
            ...subCategory,
            parentId: category.id,
          })),
        });
      });

      categoryMap.forEach((category, id) => {
        category.categories.forEach((subCategory) => {
          if (categoryMap.has(subCategory.id)) {
            const existingSubCategory = categoryMap.get(subCategory.id)!;
            existingSubCategory.parentId = id;
            existingSubCategory.categories = subCategory.categories.map((subSubCategory) => ({
              ...subSubCategory,
              parentId: subCategory.id,
            }));
          } else {
            categoryMap.set(subCategory.id, subCategory);
          }
        });
      });

      return Array.from(categoryMap.values());
    }, [categories]);

    const getParentCategorySlugById = useCallback(
      (parentId: number | undefined) => categories.find((item) => item.id === parentId)?.slug,
      [categories]
    );

    const onCloseDrawers = useCallback(() => {
      onClose();
      onCloseDrawer();
    }, [onClose, onCloseDrawer]);

    const renderCategory = useCallback(
      (
        { id, categories, fileUrl: mediaPath, name, slug, parentId }: IRenderCategoryState,
        index: number
      ) => {
        const parentSlug = getParentCategorySlugById(parentId);

        const redirectTo = parentSlug
          ? `${ENotAuthNavigation.CATEGORY_OF_CARD}/${parentSlug}?sub=${slug}`
          : `${ENotAuthNavigation.CATEGORY_OF_CARD}/${slug}`;

        return (
          <li key={`${id}-${index}`}>
            <HtsLink
              label={name}
              redirectTo={redirectTo}
              typographyProps={{ variant: 'body2', fontWeight: categories.length ? '600' : '400' }}
              linkProps={{ onClick: onCloseDrawers }}
            />
          </li>
        );
      },
      [getParentCategorySlugById, onCloseDrawers]
    );

    return (
      <SwipeableDrawer
        anchor="left"
        open={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        SlideProps={{ className: styles.slideContainer }}
      >
        <div className={styles.navigationContainer}>
          <HtsButtonIcon onClick={onClose}>
            <HtslIcon icon="keyboard-backspace-left" className={styles.backspaceIcon} />
          </HtsButtonIcon>
        </div>

        <div className={styles.contentContainer}>
          <HtsTypography variant="subtitle">All Seasons</HtsTypography>

          <ul className={styles.categoriesContainer}>{renderCategories.map(renderCategory)}</ul>
        </div>
      </SwipeableDrawer>
    );
  }
);
